import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

import {
  createPCEnrollmentSession,
  getPCEnrollmentStatus,
  notifyPCEnrollmentOwner,
  pcEnrollmentInstallerUrl,
  type CreateSessionResponse,
  type PCEnrollmentStatus,
} from "../api/pcEnrollment";

// Poll every 3 s once the session is created. Backend's poll throttle
// is 120/min — well above this.
const POLL_MS = 3000;

export default function Join() {
  const [session, setSession] = useState<CreateSessionResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<PCEnrollmentStatus>("requested");
  const [error, setError] = useState<string | null>(null);
  const [downloadStarted, setDownloadStarted] = useState(false);
  // "Notify owner" sub-form: lets a PC user trigger an email to the
  // owner without needing to copy-paste the URL out of this page.
  const [officeCode, setOfficeCode] = useState("");
  const [notifyStatus, setNotifyStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [notifyError, setNotifyError] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Create the session on first mount. The PC's source IP gets captured
  // server-side here — every subsequent step (approve, download, claim)
  // is bound to that IP.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await createPCEnrollmentSession();
        if (cancelled || !mounted.current) return;
        setSession(data);
        // The QR encodes the full URL of the approve page, NOT just the
        // code. iPhone Camera / Android Camera both auto-detect URL QRs
        // and offer to open them in the default browser.
        const qr = await QRCode.toDataURL(data.approve_url, {
          width: 320,
          margin: 1,
          errorCorrectionLevel: "M",
        });
        if (!cancelled && mounted.current) setQrDataUrl(qr);
      } catch (e: unknown) {
        if (!cancelled && mounted.current) {
          const detail = (e as { response?: { data?: { detail?: string } } })
            ?.response?.data?.detail;
          setError(detail ?? "Couldn't start setup. Please refresh and try again.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll status. On approval, trigger the installer download
  // automatically — the user only has to double-click the file once it
  // lands in Downloads.
  useEffect(() => {
    if (!session) return;
    if (status === "claimed" || status === "denied" || status === "expired") {
      return;
    }
    const id = setInterval(async () => {
      try {
        const s = await getPCEnrollmentStatus(session.code);
        if (!mounted.current) return;
        setStatus(s.status);
      } catch {
        // Soft-fail; keep polling.
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [session, status]);

  // Auto-trigger download once approved. Anchor click instead of
  // window.location.href so the user stays on the /join page.
  const triggerDownload = useCallback(() => {
    if (!session || downloadStarted) return;
    setDownloadStarted(true);
    const a = document.createElement("a");
    a.href = pcEnrollmentInstallerUrl(session.code);
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [session, downloadStarted]);

  useEffect(() => {
    if (status === "approved") triggerDownload();
  }, [status, triggerDownload]);

  const submitNotify = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!session) return;
      const trimmed = officeCode.trim().toUpperCase();
      // Office codes are 6 chars from a confusion-resistant alphabet
      // (no 0/O, no 1/I/L). Match server-side gen_office_code in
      // backend/apps/tenants/utils.py exactly:
      //   digits 2-9, letters A-H, J, K, M, N, P-Z (excludes I, L, O).
      // Note: `[K-N]` would also accept L — must be explicit `KMN`.
      // Validating client-side prevents the user typing 5 chars + a
      // smart quote pasted from email and seeing the anti-enumeration
      // "Request sent" success message when nothing actually got sent.
      if (!/^[2-9A-HJKMNP-Z]{6}$/.test(trimmed)) {
        setNotifyStatus("error");
        setNotifyError(
          "That doesn't look like a valid office code. It should be 6 letters/numbers " +
            "(no 0, O, 1, I, or L). Double-check with your owner.",
        );
        return;
      }
      setNotifyStatus("sending");
      setNotifyError(null);
      try {
        await notifyPCEnrollmentOwner(session.code, trimmed);
        if (!mounted.current) return;
        // Anti-enumeration: backend returns the same response whether
        // the code matched or not. UI matches — always "sent."
        setNotifyStatus("sent");
      } catch (e: unknown) {
        if (!mounted.current) return;
        const resp = (e as { response?: { status?: number; data?: { detail?: string } } })
          ?.response;
        const detail = resp?.data?.detail;
        if (resp?.status === 429) {
          setNotifyError("Too many requests. Wait a few minutes before trying again.");
        } else {
          setNotifyError(detail ?? "Couldn't send the request. Try again in a moment.");
        }
        setNotifyStatus("error");
      }
    },
    [session, officeCode],
  );

  if (error) {
    return (
      <Layout>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="font-medium text-red-900">Setup couldn't start.</p>
          <p className="mt-1 text-sm text-red-800">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="no-tap-highlight mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Try again
          </button>
        </div>
      </Layout>
    );
  }

  if (!session || !qrDataUrl) {
    return (
      <Layout>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
          <p className="mt-3 text-sm text-slate-500">Starting setup…</p>
        </div>
      </Layout>
    );
  }

  if (status === "claimed") {
    return (
      <Layout>
        <div className="rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
          <p className="text-lg font-medium text-green-900">All set!</p>
          <p className="mt-1 text-sm text-green-800">
            This computer is now part of your office network. You can close this
            tab.
          </p>
        </div>
      </Layout>
    );
  }

  if (status === "denied" || status === "expired") {
    return (
      <Layout>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
          <p className="font-medium text-slate-900">
            {status === "denied" ? "Setup was cancelled." : "This setup expired."}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Start over to try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="no-tap-highlight mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Start over
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h1 className="text-xl font-semibold text-slate-900">
          Add this computer to your office
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Scan this QR code with the phone you use for KraetdNet.
        </p>

        <div className="mt-6 flex flex-col items-center gap-4 rounded-2xl bg-slate-50 p-6">
          <img
            src={qrDataUrl}
            alt="Scan this with your phone"
            className="h-auto w-72 max-w-full"
          />
          <div className="text-center">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Or type this on your phone
            </p>
            <p className="mt-1 font-mono text-sm text-slate-900 break-all">
              {session.approve_url}
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border-2 border-amber-200 bg-amber-50 p-5 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-amber-900">
            Code to confirm on your phone
          </p>
          <p className="mt-2 font-mono text-4xl font-bold tracking-widest text-amber-900">
            {session.oob_code}
          </p>
          <p className="mt-2 text-xs text-amber-900/80">
            Your phone will ask for this 4-digit code. Don't share it with
            anyone — it proves this is the PC you're sitting at.
          </p>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-medium text-slate-900">
            Owner not here to scan?
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Enter your office code and we'll email your owner a link to
            approve from anywhere. They'll still ask you for the 4-digit
            code above.
          </p>
          {notifyStatus === "sent" ? (
            <div className="mt-4 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-900">
              ✓ Request sent. Check with your owner — the email may take a
              minute to arrive.
            </div>
          ) : (
            <form onSubmit={submitNotify} className="mt-4 flex flex-col gap-3 sm:flex-row">
              <label htmlFor="office-code" className="sr-only">
                Office code
              </label>
              <input
                id="office-code"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                value={officeCode}
                onChange={(e) => {
                  setOfficeCode(e.target.value);
                  if (notifyStatus === "error") setNotifyStatus("idle");
                }}
                placeholder="Office code"
                maxLength={8}
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm uppercase tracking-widest text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none"
                disabled={notifyStatus === "sending"}
              />
              <button
                type="submit"
                disabled={notifyStatus === "sending" || officeCode.trim().length === 0}
                className="no-tap-highlight rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {notifyStatus === "sending" ? "Sending…" : "Send request"}
              </button>
            </form>
          )}
          {notifyStatus === "error" && notifyError && (
            <p className="mt-2 text-xs text-red-700">{notifyError}</p>
          )}
        </div>

        <StatusLine status={status} />

        <p className="mt-6 text-center text-xs text-slate-500">
          This page will keep waiting. Don't close the tab until the
          installer starts downloading.
        </p>
      </div>
    </Layout>
  );
}

function StatusLine({ status }: { status: PCEnrollmentStatus }) {
  if (status === "approved") {
    return (
      <div className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-green-100 px-4 py-3 text-sm text-green-900">
        <span className="text-base">✓</span> Approved — your installer is
        downloading. Double-click it when it finishes.
      </div>
    );
  }
  return (
    <div className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
      <span className="h-3 w-3 animate-pulse rounded-full bg-amber-400" />
      Waiting for your owner to approve on their phone…
    </div>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4 sm:p-8">
      <div className="w-full max-w-xl">
        <header className="mb-6 text-center">
          <div className="text-lg font-semibold text-slate-900">KraetdNet</div>
        </header>
        {children}
      </div>
    </main>
  );
}
